import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Gallery.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// Category configuration with Font Awesome icons
const CATEGORY_CONFIG = {
    'events': { icon: 'fa-calendar-alt', label: 'Events' },
    'celebration': { icon: 'fa-party-horn', label: 'Celebration' },
    'awards': { icon: 'fa-trophy', label: 'Awards' },
    'sports': { icon: 'fa-running', label: 'Sports' },
    'academics': { icon: 'fa-graduation-cap', label: 'Academics' },
    'campus': { icon: 'fa-school', label: 'Campus' },
    'donation': { icon: 'fa-utensils', label: 'Food Donation' },
    'hostel': { icon: 'fa-hotel', label: 'Hostel' },
    'activities': { icon: 'fa-hand-sparkles', label: 'Activities' },
    'computers': { icon: 'fa-laptop', label: 'Computers' },
    'others': { icon: 'fa-image', label: 'Others' }
};

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Lightbox Component ---
function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button className="lightbox-close" onClick={onClose}>×</button>
        <button className="lightbox-nav prev" onClick={onPrev}>‹</button>
        <button className="lightbox-nav next" onClick={onNext}>›</button>
        <img 
          src={images[currentIndex]?.url || images[currentIndex]} 
          alt={images[currentIndex]?.title || `Gallery ${currentIndex + 1}`} 
          className="lightbox-image"
        />
        <div className="lightbox-counter">
          {currentIndex + 1} / {images.length}
        </div>
        {images[currentIndex]?.title && (
          <div className="lightbox-title">
            {images[currentIndex].title}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Gallery Component ---

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch gallery data from API
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const result = await apiService.fetchGalleryMedia();
        
        console.log('🖼️ Gallery response:', result);
        
        if (result?.status === 'success' && result?.data?.length > 0) {
          // Process gallery items
          const items = result.data;
          setGalleryItems(items);
          
          // Extract all images with their metadata
          const images = [];
          const categorySet = new Set();
          
          items.forEach(item => {
            // Determine category - use item.category or fallback to 'others'
            let category = item.category || 'others';
            
            // Check if category exists in config, if not, use 'others'
            if (!CATEGORY_CONFIG[category]) {
              category = 'others';
            }
            
            // Only process if images exist
            if (item.images && item.images.length > 0) {
              item.images.forEach(img => {
                images.push({
                  url: img,
                  title: item.title_en || item.title || '',
                  category: category,
                  id: item.id,
                  gallery_code: item.gallery_code
                });
              });
              
              // Add category to set if it has images
              categorySet.add(category);
            }
          });
          
          setAllImages(images);
          
          // Build available categories list with 'all' first
          const sortedCategories = ['all', ...Array.from(categorySet).sort()];
          setAvailableCategories(sortedCategories);
          
          console.log('✅ Gallery loaded:', images.length, 'images in', categorySet.size, 'categories');
          console.log('📂 Available categories:', sortedCategories);
        } else {
          console.warn('⚠️ No gallery data found');
          setError('No gallery data available');
          setAvailableCategories(['all']);
        }
      } catch (err) {
        console.error('❌ Error fetching gallery:', err);
        setError(err.message || 'Failed to load gallery');
        setAvailableCategories(['all']);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Get category icon class
  const getCategoryIcon = (category) => {
    if (category === 'all') return 'fa-th-large';
    return CATEGORY_CONFIG[category]?.icon || 'fa-image';
  };

  // Get category label with fallback
  const getCategoryLabel = (category) => {
    if (category === 'all') return 'All Photos';
    return CATEGORY_CONFIG[category]?.label || category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Check if a category has images
  const hasCategoryImages = (category) => {
    if (category === 'all') return allImages.length > 0;
    return allImages.some(img => img.category === category);
  };

  // Filter images by category
  const filteredImages = selectedCategory === 'all' 
    ? allImages 
    : allImages.filter(img => img.category === selectedCategory);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  // Get stats
  const getStats = () => {
    const totalImages = allImages.length;
    const totalItems = galleryItems.length;
    const categoryCount = availableCategories.length > 1 ? availableCategories.length - 1 : 0;
    return [
      { number: totalImages, label: 'Photos' },
      { number: totalItems, label: 'Gallery Items' },
      { number: categoryCount, label: 'Categories' }
    ];
  };

  const stats = getStats();

  return (
    <>
      <Header />
      <div className="gallery-page devnar">
        <a className="skip-link" href="#main-gallery">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-gallery">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-gallery">
            <Reveal>
              <span className="hero-badge">Moments</span>
              <h1>Gallery</h1>
              <p className="hero-subtitle">
                Moments from campus, sports, events, and daily life at Devnar Foundation.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-gallery" className="wrap page-content-gallery">

          {/* --- Gallery Intro --- */}
          <section className="gallery-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Visual Stories</span>
              <h2>Glimpses of Devnar</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Explore the vibrant life at Devnar Foundation — from classrooms and computer labs 
                to sports, events, and the joy of learning.
              </p>
            </Reveal>
            
            <div className="gallery-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <span className="stat-number">{stat.number}+</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* --- Category Filter - Only show categories with images --- */}
          {availableCategories.length > 1 && (
            <div className="gallery-filter">
              {availableCategories.map((category) => (
                // Only render categories that have images (or 'all' which is always shown if there are images)
                (category === 'all' || hasCategoryImages(category)) && (
                  <button
                    key={category}
                    className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <i className={`fas ${getCategoryIcon(category)}`}></i>
                    <span className="filter-label">
                      {getCategoryLabel(category)}
                    </span>
                    
                  </button>
                )
              ))}
            </div>
          )}

          {/* --- Gallery Grid --- */}
          <section className="gallery-grid-section">
            {loading ? (
              <div className="gallery-loader">
                <div className="loader-spinner"></div>
                <p>Loading gallery...</p>
              </div>
            ) : error ? (
              <div className="gallery-error">
                <p>Unable to load gallery. Please try again later.</p>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="gallery-empty">
                <p>No images found in this category.</p>
              </div>
            ) : (
              <div className="gallery-grid">
                {filteredImages.map((image, index) => (
                  <Reveal 
                    key={image.id + index} 
                    className="gallery-item" 
                    delay={(index % 12) * 50}
                  >
                    <div 
                      className="gallery-item-inner"
                      onClick={() => openLightbox(index)}
                    >
                      <img 
                        src={image.url} 
                        alt={image.title || `Gallery ${index + 1}`} 
                        loading="lazy"
                      />
                      <div className="gallery-item-overlay">
                        <i className="fas fa-search-plus"></i>
                        <span className="view-text">View</span>
                      </div>
                      {image.title && (
                        <div className="gallery-item-title">
                          {image.title}
                        </div>
                      )}
                      {/* Category badge on image */}
                      {image.category && image.category !== 'others' && (
                        <div className="gallery-item-category-badge">
                          <i className={`fas ${getCategoryIcon(image.category)}`}></i>
                          {getCategoryLabel(image.category)}
                        </div>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </section>

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-gallery">
            <div className="cta-content">
              <span className="cta-eyebrow">Visit Us</span>
              <h2>Experience Devnar in Person</h2>
              <p>
                Come see the vibrant campus, meet our students, and witness the transformative 
                work happening at Devnar Foundation.
              </p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="/contact">Plan a Visit</a>
                <a className="btn btn-outline btn-outline-light" href="/volunteer">Become a Volunteer</a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>

      {/* --- Lightbox --- */}
      {lightboxOpen && filteredImages.length > 0 && (
        <Lightbox
          images={filteredImages}
          currentIndex={currentImageIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}

      <Footer />
    </>
  );
}